import React, { useMemo } from 'react'
import { useAppSelector } from 'reduxStore/store'
import Storage from 'storage/auth'
import { IProssionalRoleTypeEnum } from 'types/user/enum'
import { IModulePermissionType } from 'types/establishment/types'

/**
 * IPermissionByRoleProps interface defines the properties for the PermissionByRole component.
 */
interface IPermissionByRoleProps extends React.PropsWithChildren {
  /** The roles allowed to access the children components */
  rolesAllowed: IProssionalRoleTypeEnum[]
}

/**
 * IPermissionByModuleNameProps interface defines the properties for the PermissionByModuleName component.
 */
interface IPermissionByModuleNameProps extends React.PropsWithChildren {
  /** The module permissions required to access the children components */
  modulesRequired: IModulePermissionType[]
  /** The condition to restrict access based on multiple modules ('and' | 'or') */
  restrictedCondition?: 'and' | 'or'
}

/**
 * PermissionByRole component
 * This component checks if the user's role is allowed to access the children components.
 * @param {IPermissionByRoleProps} props - The properties for the PermissionByRole component.
 * @returns {JSX.Element} The rendered children if the user's role is allowed, otherwise an empty fragment.
 */
export const PermissionByRole: React.FC<IPermissionByRoleProps> = ({
  children,
  rolesAllowed
}) => {
  const role_type = useAppSelector(state => state.establishment.data?.role_type)

  if (!role_type) {
    return null
  }

  if (rolesAllowed.includes(role_type)) return <>{children}</>

  return null
}

/**
 * PermissionByModuleName component
 * This component checks if the establishment has the required module permissions.
 * @param {IPermissionByModuleNameProps} props - The properties for the PermissionByModuleName component.
 * @returns {JSX.Element} The rendered children if the establishment has the required permissions, otherwise an empty fragment.
 */
export const PermissionByModuleName: React.FC<IPermissionByModuleNameProps> = ({
  children,
  modulesRequired,
  restrictedCondition = 'or'
}) => {
  const establishmentModules = useAppSelector(
    state => state.establishment.data?.modules_permission
  )

  // Memoize the modules array to prevent unnecessary re-renders
  const memoizedModules = useMemo(() => {
    return establishmentModules || []
  }, [establishmentModules])

  // Check if the required module is present in establishment permissions
  const hasRequiredModule = useMemo(() => {
    return restrictedCondition == 'or'
      ? modulesRequired.some(module => memoizedModules.includes(module))
      : modulesRequired.every(module => memoizedModules.includes(module))
  }, [modulesRequired, memoizedModules, restrictedCondition])

  if (!hasRequiredModule && modulesRequired.length > 0) {
    return null
  }

  return <>{children}</>
}

export const useSuperAdmin = () => {
  return !!Storage.user.get()?.super_admin
}

/**
 * PermissionByAdminRole component
 * This component checks if the user is a super admin.
 * If the user is a super admin, it renders the children components.
 * @param {React.PropsWithChildren} props - The properties for the PermissionByAdminRole component.
 * @returns {JSX.Element} The rendered children if the user is a super admin, otherwise an empty fragment.
 */
export const PermissionByAdminRole: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const isSuperAdmin = useSuperAdmin()

  if (!isSuperAdmin) {
    return null
  }

  return <>{children}</>
}

export const wrapWithPermissionsByRole = (
  component: React.ReactElement,
  condition: boolean,
  allowedRoles: IProssionalRoleTypeEnum[]
) => {
  const key = component.key

  if (condition) {
    return (
      <PermissionByRole key={key} rolesAllowed={allowedRoles}>
        {React.cloneElement(component, { key: undefined })}
      </PermissionByRole>
    )
  }

  return component
}

export const wrapWithPermissionsByModuleName = (
  component: React.ReactElement,
  condition: boolean,
  requiredModules: IModulePermissionType[],
  restrictedCondition: 'and' | 'or' = 'or'
) => {
  const key = component.key

  if (condition) {
    return (
      <PermissionByModuleName
        key={key}
        modulesRequired={requiredModules}
        restrictedCondition={restrictedCondition}
      >
        {React.cloneElement(component, { key: undefined })}
      </PermissionByModuleName>
    )
  }

  return component
}

export const wrapWithPermissionsByAdminRole = (
  component: React.ReactElement,
  isAdmin: boolean
) => {
  const key = component.key

  if (isAdmin) {
    return (
      <PermissionByAdminRole key={key}>
        {React.cloneElement(component, { key: undefined })}
      </PermissionByAdminRole>
    )
  }

  return component
}

export default PermissionByRole
